import { supabase } from '@/services/supabase/client';

import { AuthClientError, classifyProfileWriteError } from './auth-error';

import type {
  AccountsClient,
  AuthClient,
  AuthCredentials,
  AuthUser,
  NewProfileRow,
  ProfilePatch,
  ProfileRow,
  VolunteerFunctionRow,
} from './types';

/**
 * Adapter de produção da porta de contas, sobre o Supabase (schema v2).
 *
 * ## O que este arquivo faz e o que ele não faz
 *
 * Ele traduz: chamada do Supabase → linha crua, e erro do Supabase → erro tipado
 * da porta. Nada mais. Regra de negócio mora em `auth.service.ts`, e mapeamento
 * linha → domínio também — se algo aqui começar a decidir o que gravar, a porta
 * virou service e o teste do service parou de cobrir o comportamento real.
 *
 * ## Por que a mensagem de erro nasce em português já aqui
 *
 * A borda que traduz erro é o `onError` global do QueryClient, e ele lê
 * `error.message` (docs/04-estado-e-dados.md). O Supabase responde em inglês —
 * "Invalid login credentials", "User already registered" —, então deixar o erro
 * cru subir colocaria inglês no meio de uma interface em português.
 */

/** Mensagem genérica para falha que não temos como classificar (rede, 5xx, bug). */
const UNEXPECTED_MESSAGE = 'Não foi possível concluir a operação. Tente novamente.';

/**
 * Preserva o erro original em `cause` em vez de descartá-lo.
 *
 * O usuário vê a mensagem em português; quem estiver depurando ainda alcança o
 * erro do Supabase pela cadeia de causas.
 */
function unexpected(cause: unknown): Error {
  return new Error(UNEXPECTED_MESSAGE, { cause });
}

/**
 * Erro de gravação do perfil: as recusas do banco que têm causa conhecida
 * (CPF repetido ou inválido, data de nascimento no futuro) viram erro tipado com
 * mensagem para a tela; o resto, o genérico.
 */
function profileWriteError(cause: { code?: string; message?: string }): Error {
  const code = classifyProfileWriteError(cause);

  return code ? new AuthClientError(code) : unexpected(cause);
}

/**
 * Reconhece "e-mail já cadastrado" nas duas formas que o Supabase usa.
 *
 * Com confirmação de e-mail desligada, ele responde com o código
 * `user_already_exists`. Com a confirmação ligada, ele **não** responde erro
 * nenhum: devolve um usuário de fachada com `identities` vazio, de propósito,
 * para que um atacante não consiga enumerar quais e-mails têm conta. Tratar só o
 * primeiro caso faria o cadastro parecer ter dado certo no segundo.
 */
function isAlreadyRegistered(code: string | undefined, message: string): boolean {
  return code === 'user_already_exists' || message.toLowerCase().includes('already registered');
}

export interface SupabaseAuthClient {
  auth: AuthClient;
  accounts: AccountsClient;
}

export function createSupabaseAuthClient(): SupabaseAuthClient {
  const auth: AuthClient = {
    async getCurrentUser(): Promise<AuthUser | null> {
      // `getSession` lê o token já persistido pelo client; `getUser` iria à rede
      // a cada checagem. Como isto roda no guard de rota, a diferença aparece
      // como atraso em toda navegação protegida.
      const { data, error } = await supabase.auth.getSession();

      if (error) throw unexpected(error);

      const user = data.session?.user;

      if (!user) return null;

      // ⚠️ Antes, sessão sem e-mail era tratada como "não autenticado": todo
      // caminho de entrada pedia e-mail e senha, então uma identidade sem e-mail
      // só podia ser lixo. O socorro sem conta (DV-24) criou a exceção legítima —
      // a conta anônima nasce sem e-mail e precisa valer como sessão, senão o
      // `insert` do pedido que ela acabou de abrir seria recusado pela RLS.
      //
      // O que **não** mudou: uma sessão sem e-mail e sem a marca de anônima
      // continua sendo lixo, e continua contando como ausência de sessão.
      const isAnonymous = user.is_anonymous ?? false;

      if (!user.email && !isAnonymous) return null;

      return { id: user.id, email: user.email ?? null, isAnonymous };
    },

    async signUp({ email, password }: AuthCredentials): Promise<AuthUser> {
      const { data, error } = await supabase.auth.signUp({ email, password });

      if (error) {
        if (isAlreadyRegistered(error.code, error.message)) {
          throw new AuthClientError('email_already_in_use');
        }

        throw unexpected(error);
      }

      if (!data.user) throw unexpected(new Error('signUp devolveu sucesso sem usuário.'));
      if (data.user.identities?.length === 0) throw new AuthClientError('email_already_in_use');

      // Sem sessão aqui, a RLS recusa a gravação do perfil logo em seguida — e o
      // erro que apareceria seria um "violates row-level security policy"
      // incompreensível, em vez da causa real.
      if (!data.session) throw new AuthClientError('email_confirmation_required');

      return { id: data.user.id, email: data.user.email ?? email, isAnonymous: false };
    },

    async signInWithPassword({ email, password }: AuthCredentials): Promise<AuthUser> {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        // Uma mensagem só para "e-mail não existe" e "senha errada": distingui-las
        // entrega ao atacante a confirmação de que aquele e-mail tem conta. O
        // Supabase já responde assim; o adapter preserva a propriedade.
        if (error.code === 'invalid_credentials' || error.status === 400) {
          throw new AuthClientError('invalid_credentials');
        }

        throw unexpected(error);
      }

      if (!data.user) throw new AuthClientError('invalid_credentials');

      return { id: data.user.id, email: data.user.email ?? email, isAnonymous: false };
    },

    async signOut(): Promise<void> {
      const { error } = await supabase.auth.signOut();

      if (error) throw unexpected(error);
    },

    async sendPasswordReset(email: string): Promise<void> {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`,
      });

      // Sucesso silencioso para e-mail inexistente é comportamento do Supabase, e
      // é o correto: responder diferente para conta existente enumeraria contas.
      if (error) throw unexpected(error);
    },

    async signInAnonymously(): Promise<AuthUser> {
      const { data, error } = await supabase.auth.signInAnonymously();

      if (error) {
        // O recurso é uma chave no painel do projeto (Authentication → Sign In /
        // Providers → Anonymous sign-ins). Desligado, o GoTrue responde 422 com
        // este código — e é uma falha de **configuração**, não de quem está
        // pedindo socorro. Sem o código próprio, ela chegaria na tela como
        // "tente novamente", e tentar de novo nunca funcionaria.
        if (error.code === 'anonymous_provider_disabled' || error.status === 422) {
          throw new AuthClientError('anonymous_sign_in_disabled');
        }

        throw unexpected(error);
      }

      if (!data.user)
        throw unexpected(new Error('signInAnonymously devolveu sucesso sem usuário.'));

      // Sem sessão aqui, a RLS recusaria o perfil e o pedido logo em seguida — o
      // mesmo cuidado que o `signUp` já tomava, pela mesma razão.
      if (!data.session)
        throw unexpected(new Error('signInAnonymously devolveu sucesso sem sessão.'));

      return { id: data.user.id, email: null, isAnonymous: true };
    },

    async sendSignUpLink(email: string, redirectTo: string): Promise<void> {
      // `updateUser`, e **não** `signInWithOtp`: o OTP abriria uma conta nova
      // para esse e-mail (ou entraria numa existente) e deixaria para trás a
      // conta anônima — junto com o pedido de socorro que ela acabou de gravar.
      // Aqui o e-mail é ligado ao usuário que já está na sessão, que é
      // exatamente o que "terminar o cadastro" significa.
      const { error } = await supabase.auth.updateUser({ email }, { emailRedirectTo: redirectTo });

      if (error) {
        if (isAlreadyRegistered(error.code, error.message) || error.code === 'email_exists') {
          throw new AuthClientError('email_already_in_use');
        }

        throw unexpected(error);
      }
    },

    async setPassword(password: string): Promise<void> {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) throw unexpected(error);
    },
  };

  const accounts: AccountsClient = {
    async findProfile(id: string): Promise<ProfileRow | null> {
      // `maybeSingle` e não `single`: ausência de perfil é caso ordinário — a
      // sessão pode existir antes da linha, e `single` trataria isso como erro.
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw unexpected(error);

      return data;
    },

    async createAccount(row: NewProfileRow): Promise<ProfileRow> {
      // `create_account` roda como `security invoker`: a RLS continua valendo e
      // o `id` sai de `auth.uid()` no servidor — o app nunca o envia.
      const { data, error } = await supabase.rpc('create_account', {
        p_display_name: row.display_name,
        p_email: row.email,
        p_phone_number: row.phone_number ?? undefined,
        p_age: row.age ?? undefined,
        p_gender: row.gender ?? undefined,
        p_photo_url: row.photo_url ?? undefined,
        p_is_volunteer: row.is_volunteer,
        // Os parâmetros novos de 2026-10-31 (DV-25) são todos opcionais: sem
        // eles, a RPC grava o perfil como sempre gravou.
        p_cpf: row.personal?.cpf ?? undefined,
        p_birth_date: row.personal?.birth_date ?? undefined,
        p_postal_code: row.personal?.postal_code ?? undefined,
        p_city: row.personal?.city ?? undefined,
        p_neighborhood: row.personal?.neighborhood ?? undefined,
        p_street: row.personal?.street ?? undefined,
        p_number: row.personal?.number ?? undefined,
      });

      if (error) throw profileWriteError(error);
      if (!data) throw unexpected(new Error('create_account devolveu sucesso sem linha.'));

      return data;
    },

    async isCpfAvailable(cpf: string): Promise<boolean> {
      const { data, error } = await supabase.rpc('is_cpf_available', { p_cpf: cpf });

      if (error) throw unexpected(error);

      return data;
    },

    async updateProfile(id: string, patch: ProfilePatch): Promise<ProfileRow> {
      const { data, error } = await supabase
        .from('profiles')
        .update(patch)
        .eq('id', id)
        .select('*')
        .single();

      if (error) throw profileWriteError(error);

      return data;
    },

    async listVolunteerFunctions(): Promise<VolunteerFunctionRow[]> {
      const { data, error } = await supabase
        .from('volunteer_functions')
        .select('id, slug, name, is_active, sort_order')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw unexpected(error);

      return data ?? [];
    },

    async listProfileFunctionIds(profileId: string): Promise<string[]> {
      const { data, error } = await supabase
        .from('profile_functions')
        .select('function_id')
        .eq('profile_id', profileId);

      if (error) throw unexpected(error);

      return (data ?? []).map((row) => row.function_id);
    },

    async replaceProfileFunctions(profileId: string, functionIds: string[]): Promise<void> {
      // Duas idas à rede sem transação, de propósito: a RLS de
      // `profile_functions` só deixa o dono tocar nas próprias linhas, então o
      // pior caso de falha no meio é um conjunto vazio — que a própria tela
      // corrige ao reenviar. Uma RPC transacional aqui seria peso sem risco real.
      const { error: deleteError } = await supabase
        .from('profile_functions')
        .delete()
        .eq('profile_id', profileId);

      if (deleteError) throw unexpected(deleteError);

      if (functionIds.length === 0) return;

      const { error: insertError } = await supabase
        .from('profile_functions')
        .insert(
          functionIds.map((functionId) => ({ profile_id: profileId, function_id: functionId })),
        );

      if (insertError) throw unexpected(insertError);
    },
  };

  return { auth, accounts };
}
